import React, {Key, useMemo} from "react";
import moment, {Moment} from "moment";
import {CoursePrice, DiscountUnit, EditCourseInput, EditCourseLessonInput, GetHolidaysByCompanyAndDateRangeQuery, Maybe, PaymentType, useGetHolidaysByCompanyAndDateRangeQuery} from "../../../generated/graphql";
import {ISODateString} from "../../../helpers/typeScriptHelpers";
import {RowIndex} from "../CourseViews/CourseLessonViews";

export const useHolidays = (startDateTime: ISODateString, endDateTime: ISODateString) => {
    const holidayQueryVariables = useMemo(
        () => ({
            start: startDateTime,
            end: endDateTime,
        }),
        [startDateTime, endDateTime],
    );

    const {data: holidayData, loading: loadingHolidays} = useGetHolidaysByCompanyAndDateRangeQuery({
        variables: holidayQueryVariables,
        fetchPolicy: "cache-first",
    });

    const holidays = holidayData?.holidaysByCompanyAndDateRange ?? [];

    const getHolidayInfo = (date: Moment): GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number] | undefined => {
        const allHolidaysOfDate = holidays.filter((holiday) => {
            const holidayStart = moment(holiday.start).startOf("day");
            const holidayEnd = moment(holiday.end).endOf("day");

            return moment(date).isSameOrAfter(holidayStart) && moment(date).isSameOrBefore(holidayEnd);
        });

        return allHolidaysOfDate.find((holiday) => holiday.stateCode === "CUSTOM") ?? allHolidaysOfDate[0];
    };

    return {holidays, loadingHolidays, getHolidayInfo};
};

export const renderIndex = (rowIndex: number, holiday?: GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number]) => {
    return <RowIndex rowIndex={rowIndex} holiday={holiday} />;
};

export const sortLessonsByDate = (unsortedLessons: Array<EditCourseLessonInput>) => {
    return unsortedLessons.sort((a, b) => {
        return new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime();
    });
};

export const getNewestEntry = (coursePrices?: Maybe<Array<Partial<CoursePrice>>>): number => {
    if (!coursePrices) {
        return 0;
    }
    let newestEntry = coursePrices.length - 1;

    for (let i = coursePrices.length - 1; i >= 0; i--) {
        if (!coursePrices[i].isSpecial) {
            newestEntry = i;
            break;
        }
    }

    return newestEntry;
};

export const processFormData = (values: EditCourseInput, courseId: string, initialCourseValues?: EditCourseInput) => {
    const instructorsIds = Array.isArray(values.instructors) ? values.instructors.map(({id}) => ({id})) : [];
    // Rev 3 R1 (Codex): Shape-Fallback fuer coursePrice (Singular, GetCourseByIdQuery) vs. coursePrices (Plural, EditCourseInput).
    // GetCourseByIdQuery selektiert das Feld als coursePrice (Singular, generated/graphql.tsx:10518).
    // EditCourseInput hat coursePrices (Plural). Der Helper akzeptiert jetzt beide Shapes
    // damit includedEntranceFee robust gelesen werden kann, egal welcher Aufrufer den dritten Parameter uebergibt.
    const coursePriceArray =
        ((initialCourseValues as unknown) as {coursePrice?: Array<Partial<CoursePrice>>; coursePrices?: Array<Partial<CoursePrice>>} | undefined)?.coursePrice ??
        ((initialCourseValues as unknown) as {coursePrice?: Array<Partial<CoursePrice>>; coursePrices?: Array<Partial<CoursePrice>>} | undefined)?.coursePrices;
    const latestPriceData = getNewestEntry(coursePriceArray as Array<Partial<CoursePrice>>);

    // console.log("XXX ===> values.lessons", values.lessons);

    const courseLessonInput = Array.isArray(values.lessons)
        ? values.lessons.map((lesson: EditCourseLessonInput) => ({
              //   id: lesson.id ?? `new-${uuid()}`,
              id: lesson.id,
              courseId,
              locationId: lesson.locationId,
              venueId: lesson.venueId,
              instructors: Array.isArray(lesson.instructors) ? lesson.instructors.map(({id}) => ({id})) : undefined,
              startDateTime: lesson.startDateTime,
              endDateTime: lesson.endDateTime,
          }))
        : [];

    const includedEntranceFee = (coursePriceArray as Array<Partial<CoursePrice>> | undefined)?.[latestPriceData]?.includedEntranceFee ?? null;
    const installmentRates = values.installmentRates;
    const installmentRatesWithCourseId = installmentRates?.map((rate) => ({
        ...rate,
        courseId,
    }));

    const discountUnit = values.discountUnit !== undefined && values.discountUnit !== null ? values.discountUnit : DiscountUnit.Euro;

    return {
        editCourseData: {
            courseTypeId: values.courseTypeId,
            courseNumberFormat: values.courseNumberFormat,
            customCourseNumberPrefix: values.customCourseNumberPrefix,
            locationId: values.locationId,
            venueId: values.venueId,
            coursePrices: [
                {
                    courseId,
                    grossPrice: values.fee,
                    vat: values.vat,
                    discount: values.discount,
                    discountUnit: discountUnit as DiscountUnit,
                    includedEntranceFee: includedEntranceFee ?? null,
                },
            ],
            paymentInterval: values.paymentInterval,
            cycle: values.cycle,
            startDateTime: sortLessonsByDate(courseLessonInput)[0].startDateTime,
            endDateTime: sortLessonsByDate(courseLessonInput)[0].endDateTime,
            annotation: values.annotation,
            websiteInfo: values.websiteInfo,
            additionalShortInfo: values.additionalShortInfo ?? undefined,
            minAttendees: values.minAttendees,
            maxAttendees: values.maxAttendees,
            // Review K1 (Sonnet): Felder, die bisher fehlten und still ueberschrieben werden konnten.
            maxAssistantsPerLesson:
                values.maxAssistantsPerLesson || values.maxAssistantsPerLesson === 0
                    ? Number(values.maxAssistantsPerLesson)
                    : null,
            customInvoiceDueDays: values.customInvoiceDueDays ?? null,
            maxWaitlist: values.maxWaitlist || values.maxWaitlist === 0 ? Number(values.maxWaitlist) : null,
            instructors: instructorsIds,
            lessons: courseLessonInput,
            allowMonthlyPayment: values.allowMonthlyPayment,
            allowUpFrontPayment: values.allowUpFrontPayment,
            allowMolliePayment: values.allowMolliePayment,
            // Phase 3: optionalen Modus mit-persistieren (3-Zustands-Panel).
            mollieOptional: values.mollieOptional,
            monthlySchedule: values.monthlySchedule,
            isActive: values.isActive,
            showInWeb: values.showInWeb,
            installmentRates: installmentRatesWithCourseId,
            paymentType: values.paymentType,
            // Vorkasse-Kurs: der Anker der Monatsraten steht am Kurs, nicht in der Vorkasse-Rate (#1459)
            dateOfFirstInstallment: values.paymentType === PaymentType.Monthly ? installmentRatesWithCourseId?.[0]?.installmentDue ?? values.dateOfFirstInstallment : values.dateOfFirstInstallment,
            discount: values.discount ?? null,
            discountUnit: values.discountUnit,
            allowLessonBooking: values.allowLessonBooking,
        },
        maxAttendeeAmountHasBeenIncreased: values?.maxAttendees && initialCourseValues?.maxAttendees && values.maxAttendees > initialCourseValues.maxAttendees,
        courseLessonInput,
    };
};

export const getNewLessons = (initialBookedLessons: Array<string>, selectedLessonIds: Array<string | Key>) => {
    return selectedLessonIds.filter((lessonId) => !initialBookedLessons.includes(lessonId as string));
};

export const getCanceledLessons = (initialBookedLessons: Array<string>, selectedLessonIds: Array<string | Key>) => {
    return initialBookedLessons.filter((lessonId) => !selectedLessonIds.includes(lessonId));
};

export type ScheduledPaymentType = {
    id?: string | null;
    paymentAmount: number | null;
    paymentDue: Date | string;
    paymentDone?: Date | string | null;
    type?: string | null;
};

export const getChangedPaymentIds = (
    originalPayments: Array<ScheduledPaymentType>,
    previewPayments: Array<ScheduledPaymentType>,
): {changedIds: Set<string>; addedIds: Set<string>; removedIds: Set<string>} => {
    const changedIds = new Set<string>();
    const addedIds = new Set<string>();
    const removedIds = new Set<string>();

    // Helper: Normalisiere Datum für Vergleich (normalisiert auf Tagesgenauigkeit)
    const normalizeDate = (date: Date | string): number => {
        const d = typeof date === "string" ? new Date(date) : date;
        const normalized = new Date(d.getFullYear(), d.getMonth(), d.getDate());

        return normalized.getTime();
    };

    // Helper: Erstelle einen eindeutigen Key basierend auf Datum+Betrag (für Matching ohne ID)
    const getDateAmountKey = (payment: ScheduledPaymentType): string => {
        const dueDate = normalizeDate(payment.paymentDue);
        const amount = Math.round((payment.paymentAmount ?? 0) * 100) / 100; // Runde auf 2 Dezimalstellen

        return `${dueDate}-${amount}`;
    };

    // Erstelle Maps für schnellen Zugriff
    // Map 1: Nach ID (falls vorhanden)
    const originalMapById = new Map<string, ScheduledPaymentType>();
    const previewMapById = new Map<string, ScheduledPaymentType>();

    // Map 2: Nach Datum+Betrag (für Matching ohne ID)
    const originalMapByDateAmount = new Map<string, ScheduledPaymentType>();
    const previewMapByDateAmount = new Map<string, ScheduledPaymentType>();

    originalPayments.forEach((p) => {
        if (p.id) {
            originalMapById.set(p.id, p);
        }
        originalMapByDateAmount.set(getDateAmountKey(p), p);
    });

    previewPayments.forEach((p) => {
        if (p.id) {
            previewMapById.set(p.id, p);
        }
        previewMapByDateAmount.set(getDateAmountKey(p), p);
    });

    // Geänderte Raten finden
    originalPayments.forEach((original) => {
        let preview: ScheduledPaymentType | undefined;
        let matchKey: string | undefined;

        // Versuche zuerst über ID zu matchen
        if (original.id) {
            preview = previewMapById.get(original.id);
            matchKey = original.id;
        }

        // Falls kein Match über ID, versuche über Datum+Betrag
        if (!preview) {
            const dateAmountKey = getDateAmountKey(original);

            preview = previewMapByDateAmount.get(dateAmountKey);
            matchKey = dateAmountKey;
        }

        if (preview && matchKey) {
            // Rate existiert in beiden, prüfe auf Änderungen
            const originalAmount = Math.round((original.paymentAmount ?? 0) * 100) / 100;
            const previewAmount = Math.round((preview.paymentAmount ?? 0) * 100) / 100;
            const originalDue = normalizeDate(original.paymentDue);
            const previewDue = normalizeDate(preview.paymentDue);

            if (Math.abs(previewAmount - originalAmount) > 0.01 || originalDue !== previewDue) {
                // Verwende ID falls vorhanden, sonst Datum+Betrag Key
                const keyToAdd = original.id ?? matchKey;

                changedIds.add(keyToAdd);
            }
        } else {
            // Rate wurde entfernt
            const keyToAdd = original.id ?? getDateAmountKey(original);

            removedIds.add(keyToAdd);
        }
    });

    // Neue Raten finden
    previewPayments.forEach((preview) => {
        let found = false;

        // Versuche zuerst über ID zu matchen
        if (preview.id) {
            found = originalMapById.has(preview.id);
        }

        // Falls kein Match über ID, versuche über Datum+Betrag
        if (!found) {
            const dateAmountKey = getDateAmountKey(preview);

            found = originalMapByDateAmount.has(dateAmountKey);
        }

        if (!found) {
            // Rate ist neu
            const keyToAdd = preview.id ?? getDateAmountKey(preview);

            addedIds.add(keyToAdd);
        }
    });

    return {changedIds, addedIds, removedIds};
};
